/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useMemo, useState } from 'react';
import {
  BackButton,
  useFetchImmutable,
  useApiClient,
} from '@marageti/z4-lib';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { TripRequest, Destination, ReferralType } from '@marageti/z4-sdk/lib/travel';
import { Traveler } from '@marageti/z4-sdk/lib/people';
import {
  Alert,
  AlertTitle,
  Box,
  Container,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { SubmitHandler, FieldValues } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useAuthStore } from '../../../store';
import TripRequestForm from '../../../components/trip-request-form';
import useTRFDestinations from '../../../hooks/use-trf-destinations';

const DEFAULT_VALUES = {
  sendEmail: true,
  numberOfChildren: 0,
  numberOfAdults: 2,
  totalNights: 5,
};

const CreateContainer = () => {
  const { enqueueSnackbar } = useSnackbar();
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const { loggedInTeamMember } = useAuthStore();

  // Traveler Create State
  const [travelerCreateError, setTravelerCreateError] = useState<ApiError>();
  // Submission error state
  const [submissionError, setSubmissionError] = useState<ApiError>();

  // Loads destinations list for TRF
  const {
    data: trfDestinations, isLoading: isTRFDestinationsLoading, error: dataLoadError,
  } = useTRFDestinations();

  const assignToSelf = async (tripRequestId:string) => {
    try {
      if (tripRequestId && loggedInTeamMember) {
        await apiClient.tripRequestsClient.putTripRequestAssignment(tripRequestId, {
          id: tripRequestId,
          assignedToName: loggedInTeamMember.fullName,
          assignedTo: loggedInTeamMember.id,
        });
      }
    } catch (error) {
      setSubmissionError(error as ApiError);
    }
  };

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    const {
      sendEmail, traveler, referringPartner, ...rest
    } = data;
    const tr: TripRequest = {
      travelers: [traveler as Traveler],
      ...rest,
    } as TripRequest;
    if (referringPartner) {
      tr.referralId = referringPartner.id;
      tr.referralType = ReferralType.OneTimePartnerReferral;
    }
    try {
      const trRes = await apiClient.tripRequestsClient.postTripRequest(tr, { params: { sendEmail } });
      await assignToSelf(trRes.id);
      enqueueSnackbar('Trip request created', { variant: 'success' });
      navigate(`/trip-requests/${trRes.id}`);
    } catch (e) {
      if ((e as ApiError).code === 401) navigate('/logout');
      setSubmissionError(e as ApiError);
    }
  };

  const onCancel = () => {
    navigate(-1);
  };

  const handleCreateTraveler = async (data: any, callback?: (v:any) => void) => {
    const {
      givenName, familyName, email, phone, timeZone, tags, referringPartner, sendEmail, countryOfResidence,
    } = data;

    const travelerRequest = {
      givenName,
      familyName,
      email,
      phone,
      timeZone,
      countryOfResidence,
      tags: tags?.map((tag: any) => tag.value) || [],
      referringPartnerId: referringPartner?.id || null,
      userInfo: [{
        roles: [],
      }],
    };

    try {
      setTravelerCreateError(undefined);
      const createdTraveler = await apiClient.peopleClient.postTraveler(travelerRequest, { params: { sendEmail } });
      if (callback) { callback(createdTraveler); }
    } catch (error: any) {
      setTravelerCreateError(error);
    }
  };

  return (
    <Container
      maxWidth="sm"
      sx={{ pb: 8 }}
    >
      <Box
        sx={{
          py: {
            desktop: 6,
            mobile: 3,
          },
        }}
      >
        <BackButton
          defaultPath="/trip-requests/all"
          edge="start"
          sx={{
            mb: 2,
          }}
        />
        <Typography variant="h2">New Trip Request</Typography>
        {(dataLoadError) && (
          <Alert severity="error">
            <AlertTitle>There was an error on our end.</AlertTitle>
            {`Code: ${dataLoadError?.code || 'Client Error'}: ${dataLoadError.message}`}
            <br />
            {dataLoadError.id && `ID: ${dataLoadError.id}`}
          </Alert>
        )}
        {(isTRFDestinationsLoading) ? (
          <Stack spacing={2} mt="20px">
            {Array(10).fill(null).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
              <Skeleton key={index} variant="rounded" height="43px" width="100%" />
            ))}
          </Stack>
        ) : (
          <TripRequestForm
            destinationsList={trfDestinations || []}
            onCancel={onCancel}
            onSubmit={onSubmit}
            submissionError={submissionError}
            defaultValues={{ ...DEFAULT_VALUES }}
            handleCreateTraveler={handleCreateTraveler}
            travelerCreateError={travelerCreateError}
            showSendEmail
          />
        )}
      </Box>
    </Container>
  );
};

export default CreateContainer;
